import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  Flex,
  Stack,
  Text,
  Textarea,
  SimpleGrid,
  ActionIcon,
  Select,
  MultiSelect,
} from '@mantine/core';
import { IconArrowsLeftRight, IconCheck } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { LockToggle } from './LockToggle';
import { PreserveAsIsToggle } from './PreserveAsIsToggle';
import { ConditionalSectionSettings } from './ConditionalSectionSettings';
import { PageBreakToggle } from './PageBreakToggle';
import { TemplateSection } from 'src/data-capture/hooks/templates/useTemplates';
import { TableOfContentsSettings } from 'src/company/templates/components/TableOfContentsSettings';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { ClientListItem } from 'src/admin/users/types';
import { SectionOptions } from 'src/company/templates/hooks/useSectionOptions';

const PREVIEW_MIN_WIDTH = 280;
const LEFT_MIN_WIDTH = 480;
const HANDLE_WIDTH = 8;

type PreviewResize = {
  isPreviewResizable: boolean;
  previewWidth: number | null;
  isDraggingPreview: boolean;
  togglePreviewResize: () => void;
  handlePreviewResizeMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  previewContainerRef: React.MutableRefObject<HTMLDivElement | null>;
};

function usePreviewResize(): PreviewResize {
  const [isPreviewResizable, setIsPreviewResizable] = useState(false);
  const [previewWidth, setPreviewWidth] = useState<number | null>(null);
  const [isDraggingPreview, setIsDraggingPreview] = useState(false);
  const previewWidthRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingPreviewRef = useRef(false);
  const bodyUserSelectRef = useRef<string>('');
  const bodyCursorRef = useRef<string>('');

  useEffect(() => {
    previewWidthRef.current = previewWidth;
  }, [previewWidth]);

  const stopPreviewDragging = useCallback(() => {
    if (!isDraggingPreviewRef.current) {
      return;
    }

    isDraggingPreviewRef.current = false;
    setIsDraggingPreview(false);

    if (bodyUserSelectRef.current) {
      document.body.style.userSelect = bodyUserSelectRef.current;
    } else {
      document.body.style.removeProperty('user-select');
    }

    if (bodyCursorRef.current) {
      document.body.style.cursor = bodyCursorRef.current;
    } else {
      document.body.style.removeProperty('cursor');
    }
  }, []);

  useEffect(() => {
    if (!isPreviewResizable) {
      stopPreviewDragging();
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!isDraggingPreviewRef.current || !containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const computedStyles = window.getComputedStyle(container);
      const gapValue = parseFloat(computedStyles.columnGap || computedStyles.gap || '0');
      const gap = Number.isFinite(gapValue) ? gapValue : 0;

      let nextWidth = containerRect.right - event.clientX - HANDLE_WIDTH / 2 - gap;
      const maxPreviewWidth = containerRect.width - LEFT_MIN_WIDTH - HANDLE_WIDTH - gap;
      const effectiveMax = Math.max(PREVIEW_MIN_WIDTH, maxPreviewWidth);

      if (Number.isFinite(nextWidth)) {
        nextWidth = Math.min(Math.max(nextWidth, PREVIEW_MIN_WIDTH), effectiveMax);
        setPreviewWidth(nextWidth);
      }
    };

    const handleMouseUp = () => {
      stopPreviewDragging();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      stopPreviewDragging();
    };
  }, [isPreviewResizable, stopPreviewDragging]);

  useEffect(() => {
    if (!isPreviewResizable || previewWidthRef.current !== null || !previewContainerRef.current) {
      return;
    }

    const measuredWidth = previewContainerRef.current.getBoundingClientRect().width;
    if (Number.isFinite(measuredWidth)) {
      setPreviewWidth(measuredWidth);
    }
  }, [isPreviewResizable]);

  useEffect(() => {
    if (!isPreviewResizable) {
      return;
    }

    const clampWidthToBounds = () => {
      if (!containerRef.current || previewWidthRef.current === null) {
        return;
      }

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const computedStyles = window.getComputedStyle(container);
      const gapValue = parseFloat(computedStyles.columnGap || computedStyles.gap || '0');
      const gap = Number.isFinite(gapValue) ? gapValue : 0;
      const maxPreviewWidth = rect.width - LEFT_MIN_WIDTH - HANDLE_WIDTH - gap;
      const effectiveMax = Math.max(PREVIEW_MIN_WIDTH, maxPreviewWidth);
      const nextWidth = Math.min(
        Math.max(previewWidthRef.current, PREVIEW_MIN_WIDTH),
        effectiveMax,
      );

      if (nextWidth !== previewWidthRef.current) {
        setPreviewWidth(nextWidth);
      }
    };

    clampWidthToBounds();
    window.addEventListener('resize', clampWidthToBounds);

    return () => {
      window.removeEventListener('resize', clampWidthToBounds);
    };
  }, [isPreviewResizable]);

  const handlePreviewResizeMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isPreviewResizable) {
        return;
      }

      event.preventDefault();

      if (previewWidthRef.current === null && previewContainerRef.current) {
        const measuredWidth = previewContainerRef.current.getBoundingClientRect().width;
        if (Number.isFinite(measuredWidth)) {
          setPreviewWidth(measuredWidth);
        }
      }

      bodyUserSelectRef.current = document.body.style.userSelect || '';
      bodyCursorRef.current = document.body.style.cursor || '';

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      isDraggingPreviewRef.current = true;
      setIsDraggingPreview(true);
    },
    [isPreviewResizable],
  );

  const togglePreviewResize = useCallback(() => {
    setIsPreviewResizable(prev => {
      const next = !prev;
      if (!next) {
        setPreviewWidth(null);
      }
      return next;
    });
  }, []);

  return {
    isPreviewResizable,
    previewWidth,
    isDraggingPreview,
    togglePreviewResize,
    handlePreviewResizeMouseDown,
    containerRef,
    previewContainerRef,
  };
}

type Props = {
  section: TemplateSection;
  onUpdateSection: (sectionId: string, updates: Partial<TemplateSection>) => void;
  onToggleComplete: (sectionId: string) => void;
  onUpdateWithMessage: (section: TemplateSection, message: string) => void;
  lastContentExternalUpdateTimestamp?: number;
  isCompleted?: boolean;
  isUpdatingMessage: boolean;
  placeholderImages?: MarkdownImage[];
  selectedClient?: ClientListItem;
  sectionOptions?: SectionOptions;
  canSetInclusionCondition?: boolean;
};

export function SectionInstructionsEditorPane({
  section,
  onUpdateSection,
  onToggleComplete,
  onUpdateWithMessage,
  isUpdatingMessage,
  lastContentExternalUpdateTimestamp,
  placeholderImages = [],
  isCompleted = false,
  selectedClient,
  sectionOptions,
  canSetInclusionCondition = false,
}: Props) {
  const [message, setMessage] = useState('');
  const prevIsUpdatingRef = useRef(false);
  const {
    isPreviewResizable,
    previewWidth,
    isDraggingPreview,
    togglePreviewResize,
    handlePreviewResizeMouseDown,
    containerRef,
    previewContainerRef,
  } = usePreviewResize();

  useEffect(() => {
    if (prevIsUpdatingRef.current && !isUpdatingMessage) {
      setMessage('');
    }
    prevIsUpdatingRef.current = isUpdatingMessage;
  }, [isUpdatingMessage]);

  const handleToggleTemplateLock = () => {
    onUpdateSection(section.id, { isTemplateLocked: !section.isTemplateLocked });
  };

  const handleToggleInstructionsLock = () => {
    onUpdateSection(section.id, { isInstructionsLocked: !section.isInstructionsLocked });
  };

  const handleTogglePreserve = (preserveAsIs: boolean) => {
    onUpdateSection(section.id, { preserveAsIs });
  };

  const handleTogglePageBreak = (addPageBreakAfterSection: boolean) => {
    onUpdateSection(section.id, { addPageBreakAfterSection });
  };

  const handleAgentChange = (value: string | null) => {
    onUpdateSection(section.id, { agent: value || undefined });
  };

  const handleDomainFilterChange = (value: string[]) => {
    onUpdateSection(section.id, { domainFilter: value.length > 0 ? value : undefined });
  };

  const handleInclusionConditionChange = (inclusionCondition: string | null) => {
    onUpdateSection(section.id, { inclusionCondition });
  };

  const isTableOfContents = section.sectionType === 'TABLE_OF_CONTENTS';

  const agentOptions = sectionOptions?.agents.map(agent => ({ value: agent, label: agent })) || [];
  const domainFilterOptions =
    sectionOptions?.domainFilters.map(filter => ({ value: filter, label: filter })) || [];

  return (
    <Stack gap="md" h="100%" style={{ minWidth: 0 }} p={'xs'} flex={1}>
      <Flex align="center" justify="space-between" style={{ minWidth: 0 }}>
        <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10" style={{ minWidth: 0 }} truncate>
          {section.sectionHeader}
        </Text>
        <Flex align="center" gap="xs" style={{ flexShrink: 0 }}>
          <Button
            variant={isCompleted ? 'secondary' : 'primary'}
            size="xs"
            onClick={() => onToggleComplete(section.id)}
            leftSection={isCompleted ? <IconCheck size={14} /> : undefined}
          >
            {isCompleted ? 'Mark incomplete' : 'Mark complete'}
          </Button>
        </Flex>
      </Flex>

      {isTableOfContents ? (
        <Stack gap="md" style={{ minWidth: 0 }}>
          <TableOfContentsSettings section={section} onUpdateSection={onUpdateSection} />
          <Flex gap="md" align="flex-end" wrap="wrap">
            <PageBreakToggle
              checked={section.addPageBreakAfterSection ?? false}
              onChange={handleTogglePageBreak}
            />
          </Flex>
        </Stack>
      ) : (
        <Flex gap="md" align="flex-end" wrap="wrap">
          <PreserveAsIsToggle checked={section.preserveAsIs} onChange={handleTogglePreserve} />
          <PageBreakToggle
            checked={section.addPageBreakAfterSection ?? false}
            onChange={handleTogglePageBreak}
          />
          {sectionOptions && (
            <>
              <Select
                label="Agent"
                placeholder="Select agent"
                data={agentOptions}
                value={section.agent || null}
                onChange={handleAgentChange}
                size="xs"
                w={180}
                clearable
              />
              <MultiSelect
                label="Domain Filter"
                placeholder="Select filters"
                data={domainFilterOptions}
                value={section.domainFilter || []}
                onChange={handleDomainFilterChange}
                size="xs"
                w={250}
                clearable
              />
            </>
          )}
        </Flex>
      )}

      {!isTableOfContents && canSetInclusionCondition && (
        <ConditionalSectionSettings
          inclusionCondition={section.inclusionCondition}
          onChange={handleInclusionConditionChange}
        />
      )}

      {!isTableOfContents && (
        <Flex
          key={`${section.id}-${lastContentExternalUpdateTimestamp}`}
          ref={containerRef}
          gap="md"
          style={{ flex: 1, minWidth: 0 }}
          align="stretch"
        >
          <SimpleGrid
            cols={2}
            spacing="md"
            style={{
              flexGrow: isPreviewResizable && previewWidth !== null ? 1 : 2,
              flexShrink: 1,
              flexBasis: 0,
              minWidth: 0,
            }}
          >
            <MarkdownEditor
              key={`template-${section.id}-${lastContentExternalUpdateTimestamp}`}
              label="Template"
              defaultValue={section.templateText}
              onChange={value => onUpdateSection(section.id, { templateText: value })}
              headerRightComponent={
                <LockToggle
                  isLocked={section.isTemplateLocked ?? false}
                  onToggle={handleToggleTemplateLock}
                />
              }
              readonly={section.isTemplateLocked}
              h="100%"
              placeholderImages={placeholderImages}
            />
            <MarkdownEditor
              key={`instructions-${section.id}-${lastContentExternalUpdateTimestamp}`}
              label="Instructions"
              defaultValue={section.sectionInstructions || ''}
              onChange={value => onUpdateSection(section.id, { sectionInstructions: value })}
              headerRightComponent={
                <LockToggle
                  isLocked={section.isInstructionsLocked ?? false}
                  onToggle={handleToggleInstructionsLock}
                />
              }
              readonly={section.isInstructionsLocked}
              h="100%"
              placeholderImages={placeholderImages}
            />
          </SimpleGrid>
          {isPreviewResizable && (
            <div
              role="separator"
              aria-orientation="vertical"
              onMouseDown={handlePreviewResizeMouseDown}
              style={{
                width: HANDLE_WIDTH,
                cursor: 'col-resize',
                backgroundColor: isDraggingPreview
                  ? 'var(--mantine-color-gray-2)'
                  : 'var(--mantine-color-gray-1)',
                borderRadius: 4,
                flex: '0 0 auto',
              }}
            />
          )}
          <div
            key={`preview-${section.id}-${lastContentExternalUpdateTimestamp}`}
            ref={previewContainerRef}
            style={
              isPreviewResizable && previewWidth !== null
                ? { width: previewWidth, flex: '0 0 auto', minWidth: 0 }
                : { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 }
            }
          >
            <MarkdownEditor
              label={
                selectedClient
                  ? `Generated Preview (${selectedClient.firstName})`
                  : 'Generated Preview'
              }
              defaultValue={section.sectionPreview || ''}
              readonly={true}
              onChange={undefined}
              h="100%"
              headerRightComponent={
                <ActionIcon
                  variant="subtle"
                  color={isPreviewResizable ? 'brand' : 'gray'}
                  size="sm"
                  aria-pressed={isPreviewResizable}
                  aria-label={
                    isPreviewResizable ? 'Disable preview resize' : 'Enable preview resize'
                  }
                  onClick={togglePreviewResize}
                >
                  <IconArrowsLeftRight size={16} />
                </ActionIcon>
              }
            />
          </div>
        </Flex>
      )}

      {!isTableOfContents && (
        <Stack gap="xs">
          <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.8">
            Request changes
          </Text>
          <Text size="xs" c="gray.6">
            Ask Siesta to change how this section is generated. Leave blank to only regenerate the
            preview.
          </Text>
          <Textarea
            placeholder="Describe the changes you'd like to make to this section..."
            minRows={3}
            maxRows={5}
            autosize
            value={message}
            onChange={event => setMessage(event.currentTarget.value)}
          />
          <Button
            variant="primary"
            size="sm"
            w="fit-content"
            onClick={() => onUpdateWithMessage(section, message)}
            loading={isUpdatingMessage}
          >
            Generate
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
